import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { get } from 'lodash';

import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';
import { ERROR_STATUS, getErrorStatus } from '@futrli/errors';
import { AccountCategory } from '@futrli-sdk/predict';
import { Logger } from '@futrli/shared-services';

import { useRouteIds } from '../../../../hooks/useRouteIds';
import { useCoaApiView } from '../../../../chart-of-accounts/hooks/useCoaApiView';
import { trackEvent } from '../../../../core/tracker';
import { TRACKING_EVENTS } from '../../../../core/constants';
import { COADisplayAccount } from '../../../../chart-of-accounts/types';
import { useSwitchRuleForm } from './use-switch-rule-form';
import { SwitchRuleFormScreen } from './switch-rule-form-screen';
import { SwitchRuleSuccessScreen } from './switch-rule-success-screen';
import { SwitchRuleLoadingScreen } from './switch-rule-loading-screen';

type Screen = 'form' | 'saving' | 'saved';

export interface SwitchRuleWizardContainerProps {
  onClose: () => void;
  account: COADisplayAccount;
}

export const SwitchRuleWizardContainer = ({ onClose, account }: SwitchRuleWizardContainerProps) => {
  const { workspaceId, organisationId } = useRouteIds();
  const intl = useIntl();
  const { accountCategoryNames, getCategorizeError422Message } = useLocalisedConstantsContext();

  const { setSwitchRule, isSettingSwitchRule } = useCoaApiView(workspaceId, organisationId);

  const [screen, setScreen] = useState<Screen>('form');
  const [error, setError] = useState<string | undefined>();

  const {
    categoryToSwitchTo,
    setCategoryToSwitchTo,
    errors,
    validate,
    revalidate,
    categories,
    accountCategoryParent
  } = useSwitchRuleForm(account);

  useEffect(() => {
    if (isSettingSwitchRule) {
      setScreen('saving');
    }
  }, [isSettingSwitchRule]);

  const handleSetSwitchRule = useCallback(() => {
    if (!validate() || !categoryToSwitchTo) {
      return;
    }

    setError(undefined);

    setSwitchRule(
      { accountId: account.id, data: { category: categoryToSwitchTo } },
      {
        onSuccess: () => {
          trackEvent(TRACKING_EVENTS.USED_SWITCH_RULE_COA);
          setScreen('saved');
        },
        onError: e => {
          setScreen('form');
          Logger.error(e.message || ((e as unknown) as string));

          const errorStatus = getErrorStatus(e);

          if (errorStatus === ERROR_STATUS._404) {
            setError(
              intl.formatMessage({
                id: 'error.missing_account',
                defaultMessage: "The account you attempted to reclassify doesn't exist in the system"
              })
            );
          } else if (errorStatus === ERROR_STATUS._422) {
            const originalCategoryName = accountCategoryNames[account.category as AccountCategory];
            const newCategoryName = accountCategoryNames[categoryToSwitchTo as AccountCategory];
            const responseErrorMessage = get(e, 'response.data.error', '');

            const errorMessageToShow = getCategorizeError422Message(
              responseErrorMessage,
              originalCategoryName,
              newCategoryName
            );

            setError(errorMessageToShow);
          } else {
            setError(
              intl.formatMessage({
                id: 'reporting.try_again',
                defaultMessage: 'Try again.'
              })
            );
          }
        }
      }
    );
  }, [
    validate,
    categoryToSwitchTo,
    setSwitchRule,
    account.id,
    account.category,
    intl,
    accountCategoryNames,
    getCategorizeError422Message
  ]);

  return (
    <>
      {screen === 'form' && (
        <SwitchRuleFormScreen
          onClose={onClose}
          onSave={handleSetSwitchRule}
          error={error}
          validationErrors={errors}
          switchRuleCategory={categoryToSwitchTo}
          setSwitchRuleCategory={setCategoryToSwitchTo}
          categories={categories}
          revalidate={revalidate}
          accountCategoryParent={accountCategoryParent}
          account={account}
        />
      )}
      {screen === 'saving' && <SwitchRuleLoadingScreen />}
      {screen === 'saved' && <SwitchRuleSuccessScreen onClose={onClose} />}
    </>
  );
};
